/* global Stripe */

"use strict";

import { loadConnectAndInitialize } from "@stripe/connect-js";

const REQUEST_ANIMATION_FRAME =
  window.requestAnimationFrame ||
  window.mozRequestAnimationFrame ||
  window.webkitRequestAnimationFrame ||
  window.msRequestAnimationFrame;

const STRIPE_FONTS = [
  {
    cssSrc:
      "https://fonts.googleapis.com/css2?family=Wix+Madefor+Text:ital,wght@0,400..800;1,400..800&display=swap",
  },
];

function getTextMutedColor() {
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue("--text-muted")
      .trim() || "#43739e"
  );
}

function getStripeAppearance(textMutedColor) {
  return {
    theme: "flat",
    variables: {
      borderRadius: "3px",
      colorBackground: "#FFFFFF",
      colorPrimary: "#1890FF",
      colorText: "#1D2334",
      colorTextPlaceholder: "#497AA5",
      colorTextSecondary: "#497AA5",
      fontFamily: "Wix Madefor Text",
      fontSizeBase: "0.875rem",
      colorIconTabSelected: "#FFFFFF",
      spacingGridRow: "1rem",
      colorIconHover: "#006cd1",
      colorIconTabHover: "#006cd1",
    },
    rules: {
      ".Tab--selected": {
        backgroundColor: "#006cd1",
        border: "0px",
        color: "#FFFFFF",
      },
      ".Tab:hover, .PickerItem:hover": {
        color: "#006cd1",
      },
      ".DropdownItem--highlight": {
        backgroundColor: "#F1F6FC",
        border: "1px solid #81AFE4",
      },
      ".Tab, .Input, .PickerItem": {
        border: "1px solid #c4dcf8",
        boxShadow: "none",
      },
      ".Label": {
        color: textMutedColor,
        textTransform: "capitalize",
        lineHeight: "20px",
        marginBottom: "5px",
        fontSize: "1rem",
        fontWeight: "600",
      },
      ".PickerItem": {
        color: "#497aa4",
        textTransform: "uppercase",
        lineHeight: "25px",
      },
      ".PickerItem:hover": {
        backgroundColor: "#FFFFFF",
      },
      ".Tab:focus, .Tab--selected:focus": {
        outline: "none",
        boxShadow: "0px",
      },
    },
  };
}

function buildAndMountElementWithSetupIntent(
  stripeClient,
  params,
  app,
  elementsRef,
) {
  const cardElement = document.querySelector(params.selector);
  const textMutedColor = getTextMutedColor();

  const elements = stripeClient.elements({
    clientSecret: params.setupIntentSecret,
    fonts: STRIPE_FONTS,
    appearance: getStripeAppearance(textMutedColor),
  });

  elementsRef.current = elements;

  const disallowedBrands = params.acceptAmex ? [] : ["amex"];
  const paymentElement = elements.create("payment", {
    terms: {
      card: "never",
      usBankAccount: "never",
    },
    defaultValues: {
      billingDetails: {
        name: params.name,
        address: {
          postal_code: params.zip,
        },
      },
    },
    disallowedCardBrands: disallowedBrands,
    wallets: {
      applePay: "never",
      googlePay: "never",
    },
  });

  paymentElement.mount(cardElement);

  paymentElement.on("change", function (element) {
    const type = (element && element.value && element.value.type) || "";
    app.ports.stripePortOnSetupIntentChange.send(type);
  });

  return {
    stripeClient: stripeClient,
    elements: elements,
    paymentElement: paymentElement,
    options: params.options,
  };
}

function createSetupIntentMountHandler(
  app,
  hintStripePublicKey,
  stripeElements,
  elementsRef,
) {
  const mountAttemptsMax = 20;
  let mountAttempts = 0;

  function tryBuildAndMount(params) {
    const stripeClient = Stripe(hintStripePublicKey, {
      betas: ["blocked_card_brands_beta_1"],
      apiVersion: "2019-12-03",
    });
    const cardElement = document.querySelector(params.selector);

    if (cardElement) {
      REQUEST_ANIMATION_FRAME(function () {
        stripeElements[params.selector] = buildAndMountElementWithSetupIntent(
          stripeClient,
          params,
          app,
          elementsRef,
        );
      });
    } else if (mountAttempts <= mountAttemptsMax) {
      mountAttempts += 1;
      setTimeout(function () {
        tryBuildAndMount(params);
      }, 100);
    }
  }

  return tryBuildAndMount;
}

function createConfirmSetupIntentHandler(app, stripeElements, elementsRef) {
  return function (params) {
    const stripeElement = stripeElements[params.selector];

    const billingDetails =
      params.options && params.options.billingOptions
        ? {
            billing_details: {
              name: params.options.billingOptions.name,
            },
          }
        : {};

    stripeElement.stripeClient
      .confirmSetup({
        elements: elementsRef.current,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: billingDetails,
        },
        redirect: "if_required",
      })
      .then(({ error, setupIntent }) => {
        if (error) {
          app.ports.stripePortOnSetupIntentConfirmed.send({
            selector: params.selector,
            setup_intent: null,
            error: error.message,
          });
        } else {
          app.ports.stripePortOnSetupIntentConfirmed.send({
            selector: params.selector,
            setup_intent: setupIntent,
            error: null,
          });
        }
      });
  };
}

function createMountPaymentDisputesHandler(hintStripePublicKey) {
  return function (params) {
    REQUEST_ANIMATION_FRAME(function () {
      const stripeConnectInstance = loadConnectAndInitialize({
        publishableKey: hintStripePublicKey,
        fetchClientSecret: () => Promise.resolve(params.stripeClientSecret),
      });

      const paymentDisputes = stripeConnectInstance.create("payment-disputes");
      paymentDisputes.setPayment(params.chargeId);
      document.querySelector(params.selector).appendChild(paymentDisputes);
    });
  };
}

function createMountAccountManagementHandler(hintStripePublicKey) {
  return function (params) {
    REQUEST_ANIMATION_FRAME(function () {
      const container = document.querySelector(params.selector);
      if (!container) {
        return;
      }

      const existing = container.querySelector(
        "[data-stripe-account-management]",
      );
      if (existing) {
        existing.remove();
      }

      const stripeConnectInstance = loadConnectAndInitialize({
        publishableKey: hintStripePublicKey,
        fetchClientSecret: () => Promise.resolve(params.stripeClientSecret),
      });

      const accountManagement =
        stripeConnectInstance.create("account-management");
      accountManagement.setAttribute("data-stripe-account-management", "");
      container.appendChild(accountManagement);
    });
  };
}

function createMountCapitalPromoTileHandler(app, hintStripePublicKey) {
  return function (params) {
    REQUEST_ANIMATION_FRAME(function () {
      const container = document.querySelector(params.selector);
      if (!container) {
        return;
      }

      const stripeConnectInstance = loadConnectAndInitialize({
        publishableKey: hintStripePublicKey,
        fetchClientSecret: () => Promise.resolve(params.stripeClientSecret),
      });

      try {
        const capitalPromoTile = stripeConnectInstance.create(
          "capital-financing-promotion",
        );
        capitalPromoTile.setLayout("banner");

        capitalPromoTile.setOnEligibleFinancingOfferLoaded(
          ({ productType }) => {
            app.ports.stripePortOnCapitalOfferLoaded.send(productType);
          },
        );

        container.appendChild(capitalPromoTile);
      } catch (error) {
        console.warn("Capital promotion tile not available:", error.message);
      }
    });
  };
}

const StripePort = {
  add: function (app, hintStripePublicKey) {
    if (!window.Stripe) {
      return;
    }

    const stripeElements = {};
    const elementsRef = { current: null };

    app.ports.stripePortBuildAndMountElementWithSetupIntent.subscribe(
      createSetupIntentMountHandler(
        app,
        hintStripePublicKey,
        stripeElements,
        elementsRef,
      ),
    );

    app.ports.stripePortConfirmSetupIntent.subscribe(
      createConfirmSetupIntentHandler(app, stripeElements, elementsRef),
    );

    app.ports.stripePortMountPaymentDisputesComponent.subscribe(
      createMountPaymentDisputesHandler(hintStripePublicKey),
    );

    app.ports.stripePortMountAccountManagementComponent.subscribe(
      createMountAccountManagementHandler(hintStripePublicKey),
    );

    app.ports.stripePortMountCapitalPromoTile.subscribe(
      createMountCapitalPromoTileHandler(app, hintStripePublicKey),
    );

    app.ports.stripePortMountAccountOnboardingComponent.subscribe(
      function (params) {
        requestAnimationFrame(function () {
          const stripeConnectInstance = loadConnectAndInitialize({
            publishableKey: hintStripePublicKey,
            fetchClientSecret: () => Promise.resolve(params.stripeClientSecret),
          });

          const accountOnboarding =
            stripeConnectInstance.create("account-onboarding");
          accountOnboarding.setOnExit(function () {
            app.ports.stripePortOnboardingExited.send(null);
          });
          accountOnboarding.setOnStepChange(function ({ step }) {
            app.ports.stripePortOnboardingStepChanged.send(step);
          });
          document
            .querySelector(params.selector)
            .appendChild(accountOnboarding);
        });
      },
    );
  },
};

module.exports = StripePort;
